import React from 'react';
import { Link } from 'react-router-dom';
import logoBlack from '../assets/images/logo-black.png';

const MarketingLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src={logoBlack} 
                alt="BigSystemOne" 
                className="h-8 w-auto"
              />
            </div>
            <Link 
              to="/login" 
              className="text-gray-900 hover:text-gray-600 font-medium transition-colors duration-200"
            >
              Login
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Hero Section with Image */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-900 mb-6">
                The Most Advanced
                <span className="block font-normal text-gray-800">
                  Restaurant Operating System
                </span>
                <span className="block text-2xl sm:text-3xl text-gray-600 mt-4">
                  Ever Built
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8">
                AI-First cloud platform with revolutionary Edge AI technology, built by restaurant operators 
                with 30+ years of multi-national experience. The most advanced restaurant operating system ever created.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 text-base font-medium transition-colors duration-200">
                  Schedule Demo
                </button>
                <button className="border border-gray-900 text-gray-900 hover:bg-gray-50 px-8 py-3 text-base font-medium transition-colors duration-200">
                  Watch Platform Overview
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/AdobeStock_1387225614.jpeg" 
                alt="Modern restaurant with BigSystemOne POS" 
                className="rounded-lg shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-4 -right-4 bg-gray-900 text-white p-6 rounded-lg max-w-xs">
                <p className="text-sm font-medium">Trusted by enterprise restaurants across 3 continents</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-light text-gray-900 mb-1">AI-First</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Platform Architecture</div>
            </div>
            <div>
              <div className="text-3xl font-light text-gray-900 mb-1">Enterprise</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Multi-Site Architecture</div>
            </div>
            <div>
              <div className="text-3xl font-light text-gray-900 mb-1">100%</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Offline Capable</div>
            </div>
            <div>
              <div className="text-3xl font-light text-gray-900 mb-1">AI-First</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Platform Design</div>
            </div>
          </div>
        </div>
      </section>

      {/* Revolutionary POS Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Revolutionary Point of Sale</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A complete reimagining of restaurant POS - intuitive, powerful, and built for speed
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/AdobeStock_1313261700.jpeg" 
                alt="BigSystemOne POS tablet in action" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
            <div>
              <h3 className="text-2xl font-medium text-gray-900 mb-6">POS Tablet Interface</h3>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Lightning-fast touch interface designed for restaurant workflow</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Graphical table management with real-time status updates</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Offline-first architecture - works without internet</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Multi-device synchronization across all terminals</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Split checks, modifiers, and advanced order management</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Integrated payment processing with all major providers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">AI-Powered Intelligence Throughout</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              AI-First architecture with Edge AI capabilities that revolutionize restaurant operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-gray-200">
              <h3 className="text-xl font-medium text-gray-900 mb-4">AI Assistant</h3>
              <p className="text-gray-600 mb-4">
                Natural language processing for voice and text commands. Manage menus, check sales, 
                and control operations through conversational AI.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Voice-enabled operations</li>
                <li>Natural language commands</li>
                <li>Contextual assistance</li>
              </ul>
            </div>

            <div className="bg-white p-8 border border-gray-200">
              <h3 className="text-xl font-medium text-gray-900 mb-4">Predictive Analytics</h3>
              <p className="text-gray-600 mb-4">
                Advanced forecasting using weather data, local events, and historical patterns 
                to optimize staffing and inventory.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Weather impact analysis</li>
                <li>Event-based forecasting</li>
                <li>Labor optimization</li>
              </ul>
            </div>

            <div className="bg-white p-8 border border-gray-200">
              <h3 className="text-xl font-medium text-gray-900 mb-4">Menu Intelligence</h3>
              <p className="text-gray-600 mb-4">
                Automated menu image generation with DALL-E 3 and intelligent menu ingestion 
                from websites and documents.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>AI image generation</li>
                <li>Menu extraction</li>
                <li>Price optimization</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-gray-900 text-white p-8 rounded-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-medium mb-4">Edge AI Technology</h3>
                <p className="text-gray-300 mb-4">
                  Revolutionary Edge AI ensures your restaurant never stops operating. Local AI processing 
                  maintains full functionality during any connectivity issues while continuously learning from your operations.
                </p>
                <ul className="text-gray-300 space-y-2">
                  <li>Local AI processing</li>
                  <li>Internet outage resilience</li>
                  <li>Real-time decision making</li>
                  <li>Predictive maintenance</li>
                </ul>
              </div>
              <div className="text-center">
                <div className="text-6xl font-light mb-2">99.99%</div>
                <div className="text-xl">Uptime Guarantee</div>
                <div className="text-sm text-gray-400 mt-2">With Edge AI failover</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Management */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6">
                Built for Multi-Location Operations
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Manage hundreds of locations with ease. Hierarchical organization structure supports 
                enterprises, concepts, franchisees, and individual stores.
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Enterprise hierarchy management with unlimited depth</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Centralized menu management with location-specific pricing</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Cross-location reporting and analytics</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Store-specific configurations and tax rules</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Role-based access control across the organization</span>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src="/AdobeStock_1366489971.jpeg" 
                alt="Multi-location restaurant management" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Complete Platform Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Everything You Need in One Platform</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Stop paying for multiple systems. BigSystemOne includes everything to run your restaurant.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Operations */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Operations
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>Point of Sale</li>
                <li>Table Management</li>
                <li>Kitchen Display System</li>
                <li>Online Ordering</li>
                <li>QR Code Menus</li>
                <li>Inventory Management</li>
                <li>Recipe Management</li>
                <li>Vendor Management</li>
              </ul>
            </div>

            {/* Staff Management */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Staff Management
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>Employee Scheduling</li>
                <li>Time & Attendance</li>
                <li>Labor Optimization</li>
                <li>Shift Marketplace</li>
                <li>Performance Tracking</li>
                <li>Payroll Integration</li>
                <li>Compliance Management</li>
                <li>Mobile Employee App</li>
              </ul>
            </div>

            {/* Analytics */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Analytics & Intelligence
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>Real-time Dashboards</li>
                <li>Sales Analytics</li>
                <li>Menu Performance</li>
                <li>Labor Analytics</li>
                <li>Predictive Forecasting</li>
                <li>Weather Impact</li>
                <li>Custom Reports</li>
                <li>Multi-location Insights</li>
              </ul>
            </div>

            {/* Guest Experience */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Guest Experience
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>Mobile Ordering</li>
                <li>QR Code Menus</li>
                <li>Waitlist Management</li>
                <li>Reservation System</li>
                <li>Loyalty Programs</li>
                <li>Guest Feedback</li>
                <li>Marketing Tools</li>
                <li>Digital Signage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Apps Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Native Mobile Applications</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Purpose-built mobile apps for every user in your restaurant
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white p-8 rounded-lg shadow-lg mb-4">
                <img 
                  src="/AdobeStock_1433854327.jpeg" 
                  alt="POS Tablet Application" 
                  className="rounded-lg mb-4 w-full h-auto"
                />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">POS Tablets</h3>
              <p className="text-gray-600">
                Full-featured tablet interface with offline capability and real-time sync
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white p-8 rounded-lg shadow-lg mb-4">
                <img 
                  src="/AdobeStock_796122058.jpeg" 
                  alt="Employee Mobile App" 
                  className="rounded-lg mb-4 w-full h-auto"
                />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Employee App</h3>
              <p className="text-gray-600">
                Mobile app for scheduling, time tracking, and shift marketplace
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white p-8 rounded-lg shadow-lg mb-4">
                <img 
                  src="/AdobeStock_1353060705.jpeg" 
                  alt="Manager Mobile App" 
                  className="rounded-lg mb-4 w-full h-auto"
                />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Manager App</h3>
              <p className="text-gray-600">
                Real-time monitoring, approvals, and management on the go
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kitchen Intelligence */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/AdobeStock_882305683.jpeg" 
                alt="Kitchen Display System" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6">
                Kitchen & Bar Intelligence
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Transform your kitchen operations with intelligent display systems that optimize 
                production flow and reduce ticket times.
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Smart order routing based on station capacity</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Real-time production tracking and analytics</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Automated bump times and performance metrics</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Multi-language support for diverse teams</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1 block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  <span>Integration with bar display systems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Architecture */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light mb-4">Enterprise-Grade Architecture</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Built on AI-First cloud architecture with Edge AI capabilities and enterprise-grade security
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div>
              <h3 className="text-xl font-medium mb-6">Cloud Infrastructure</h3>
              <ul className="space-y-4 text-gray-300">
                <li>
                  <strong className="text-white">AI-First Cloud</strong>
                  <span className="block text-sm mt-1">Intelligent cloud architecture with AI at the core</span>
                </li>
                <li>
                  <strong className="text-white">Cloud Services</strong>
                  <span className="block text-sm mt-1">Serverless functions and databases for scalable operations</span>
                </li>
                <li>
                  <strong className="text-white">Microservices Architecture</strong>
                  <span className="block text-sm mt-1">Independent services for reliability and scale</span>
                </li>
                <li>
                  <strong className="text-white">Real-time Synchronization</strong>
                  <span className="block text-sm mt-1">Instant data propagation across all devices</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-medium mb-6">Security & Compliance</h3>
              <ul className="space-y-4 text-gray-300">
                <li>
                  <strong className="text-white">Enterprise Authentication</strong>
                  <span className="block text-sm mt-1">Secure identity management with multi-factor authentication</span>
                </li>
                <li>
                  <strong className="text-white">Role-Based Access</strong>
                  <span className="block text-sm mt-1">Granular permissions across the organization</span>
                </li>
                <li>
                  <strong className="text-white">PCI Compliance</strong>
                  <span className="block text-sm mt-1">Secure payment processing and data handling</span>
                </li>
                <li>
                  <strong className="text-white">Data Encryption</strong>
                  <span className="block text-sm mt-1">End-to-end encryption at rest and in transit</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-light mb-2">99.99%</div>
              <div className="text-sm text-gray-400">Uptime SLA</div>
            </div>
            <div>
              <div className="text-3xl font-light mb-2">&lt;100ms</div>
              <div className="text-sm text-gray-400">API Response Time</div>
            </div>
            <div>
              <div className="text-3xl font-light mb-2">Global</div>
              <div className="text-sm text-gray-400">Multi-Region Deploy</div>
            </div>
            <div>
              <div className="text-3xl font-light mb-2">24/7</div>
              <div className="text-sm text-gray-400">Monitoring & Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Proven Return on Investment</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our customers see immediate impact on their bottom line
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-4xl font-light text-gray-900 mb-2">2-3%</div>
              <div className="text-gray-600">Sales Increase</div>
              <div className="text-sm text-gray-500 mt-2">From operational efficiency</div>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-4xl font-light text-gray-900 mb-2">15%</div>
              <div className="text-gray-600">Labor Cost Reduction</div>
              <div className="text-sm text-gray-500 mt-2">AI-powered scheduling</div>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-4xl font-light text-gray-900 mb-2">50%</div>
              <div className="text-gray-600">Faster Service</div>
              <div className="text-sm text-gray-500 mt-2">Streamlined operations</div>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-4xl font-light text-gray-900 mb-2">90%</div>
              <div className="text-gray-600">Time Saved</div>
              <div className="text-sm text-gray-500 mt-2">On administrative tasks</div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Why BigSystemOne Wins</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Built by operators who understand what restaurants actually need
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-4 px-6 text-gray-900 font-medium">Feature</th>
                  <th className="py-4 px-6 text-gray-900 font-medium">BigSystemOne</th>
                  <th className="py-4 px-6 text-gray-500 font-normal">Traditional POS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 px-6 text-gray-600">Architecture</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">Cloud-Native</td>
                  <td className="py-4 px-6 text-gray-500">On-Premise or Hybrid</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">AI Integration</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">AI-First Platform</td>
                  <td className="py-4 px-6 text-gray-500">Limited or Add-on</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">Offline Capability</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">100% Functionality</td>
                  <td className="py-4 px-6 text-gray-500">Limited or None</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">Updates</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">Automatic, No Downtime</td>
                  <td className="py-4 px-6 text-gray-500">Manual, Requires Downtime</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">Pricing</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">Transparent, All-Inclusive</td>
                  <td className="py-4 px-6 text-gray-500">Hidden Fees, Per-Terminal</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">Contract</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">Month-to-Month</td>
                  <td className="py-4 px-6 text-gray-500">3-5 Year Lock-in</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-600">Support</td>
                  <td className="py-4 px-6 text-gray-900 font-medium">24/7 with AI Assistant</td>
                  <td className="py-4 px-6 text-gray-500">Business Hours Only</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Customer Success */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Trusted by Industry Leaders</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From single locations to multi-national chains, BigSystemOne powers success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <img 
                src="/AdobeStock_569908558.jpeg" 
                alt="Fine dining restaurant" 
                className="rounded-lg mb-6 w-full h-auto"
              />
              <h3 className="text-xl font-medium text-gray-900 mb-2">Fine Dining Excellence</h3>
              <p className="text-gray-600 mb-4">
                "BigSystemOne transformed our operations. The AI-powered analytics helped us reduce 
                food waste by 30% while improving service times."
              </p>
              <p className="text-sm text-gray-500">- Executive Chef, Michelin-starred restaurant</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <img 
                src="/AdobeStock_864944058.jpeg" 
                alt="Multi-location restaurant chain" 
                className="rounded-lg mb-6 w-full h-auto"
              />
              <h3 className="text-xl font-medium text-gray-900 mb-2">Enterprise Scale</h3>
              <p className="text-gray-600 mb-4">
                "Managing 150+ locations has never been easier. The real-time visibility and 
                predictive analytics are game-changers for our business."
              </p>
              <p className="text-sm text-gray-500">- COO, National Restaurant Chain</p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Simple, Fast Implementation</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Go live in days, not months. Our team ensures a smooth transition.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-medium">
                  1
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Discovery & Planning</h3>
                  <p className="text-gray-600">
                    We analyze your current operations and create a customized implementation plan
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-medium">
                  2
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Configuration & Training</h3>
                  <p className="text-gray-600">
                    Your system is configured to your exact needs while we train your team
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-medium">
                  3
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Go Live & Support</h3>
                  <p className="text-gray-600">
                    Launch with confidence knowing our team is there every step of the way
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light mb-6">Ready to Transform Your Restaurant?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join the growing number of restaurants leveraging BigSystemOne to stay ahead of the competition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 text-lg font-medium transition-colors duration-200">
              Schedule Your Demo
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-medium transition-colors duration-200">
              Download ROI Calculator
            </button>
          </div>
          <p className="text-sm text-gray-400">
            No long-term contracts • Transparent pricing • 24/7 support included
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/logo-white.png" 
                alt="BigSystemOne" 
                className="h-8 w-auto mb-4"
              />
              <p className="text-sm text-gray-400">
                The most advanced restaurant operating system ever built.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Point of Sale</li>
                <li>Kitchen Display</li>
                <li>Analytics</li>
                <li>Employee Management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>About Us</li>
                <li>Contact</li>
                <li>Careers</li>
                <li>Partners</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Documentation</li>
                <li>API Reference</li>
                <li>Support Center</li>
                <li>Status Page</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400">
              © 2024 BigSystemOne. Built by restaurant operators, for restaurant operators.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLandingPage;